import clsx from 'clsx';
import useHeroWidgetsMotion from './useHeroWidgetsMotion';
import WidgetCard from './WidgetCard';
import BalanceWidget from './BalanceWidget';
import ClockWidget from './ClockWidget';
import TrigWidget from './TrigWidget';
import LinearGraphsWidget from './LinearGraphsWidget';
import MathFloater from './MathFloater';
import styles from './HeroWidgets.module.css';

const INK = 'var(--color-text-primary)';

/** <768px: only Balance and Clock - the design handoff crops Trig/Linear graphs at this width. */
const MOBILE_CARDS = [
    {Widget: BalanceWidget, left: 56, top: 10, width: 278, rotate: 3, floatDuration: 7, floatDelay: -2},
    {Widget: ClockWidget, left: 4, top: 318, width: 316, rotate: -2, floatDuration: 7.5, floatDelay: -1},
];

const MOBILE_FLOATERS = [
    {symbol: 'π', left: -4, top: 30, size: 58, fontSize: 30, radius: '50%', bg: 'var(--color-neutral-50)', fg: INK, rotate: -10, depth: 'behind', duration: 7.4, delay: -3.2},
    {symbol: '=', left: 292, top: 300, size: 56, fontSize: 28, radius: 16, bg: 'var(--color-feedback-correct-tint)', fg: INK, rotate: 10, depth: 'behind', duration: 7, delay: -3.0},
    {symbol: '%', left: 250, top: 600, size: 52, fontSize: 26, radius: 14, bg: '#FDE8D6', fg: INK, rotate: -8, depth: 'behind', duration: 6.2, delay: -2.7},
    {symbol: '×', left: 8, top: 262, size: 44, fontSize: 24, radius: 12, bg: 'var(--color-primary-100)', fg: INK, rotate: -12, depth: 'front', duration: 5.6, delay: -2.4},
    {symbol: '+', left: 296, top: -8, size: 40, fontSize: 22, radius: '50%', bg: 'var(--home-color-orange)', fg: INK, rotate: 8, depth: 'front', duration: 5.2, delay: -2.3},
    {symbol: '÷', left: 40, top: 606, size: 48, fontSize: 26, radius: 14, bg: 'var(--home-color-orange)', fg: INK, rotate: 10, depth: 'front', duration: 6.2, delay: -2.7},
    {symbol: '√', left: 150, top: 614, size: 46, fontSize: 24, radius: 14, bg: 'var(--color-neutral-900)', fg: 'var(--home-color-orange)', rotate: -6, depth: 'front', duration: 6.6, delay: -2.9},
];

/** >=1024px: the full 4-widget cluster. */
const DESKTOP_CARDS = [
    {Widget: TrigWidget, left: 0, top: 30, width: 336, rotate: -3, floatDuration: 6, floatDelay: 0},
    {Widget: BalanceWidget, left: 362, top: 0, width: 278, rotate: 3, floatDuration: 7, floatDelay: -2},
    {Widget: ClockWidget, left: 16, top: 340, width: 340, rotate: -2, floatDuration: 7.5, floatDelay: -1},
    {Widget: LinearGraphsWidget, left: 374, top: 330, width: 266, rotate: 2.5, floatDuration: 6.8, floatDelay: -3},
];

const DESKTOP_FLOATERS = [
    {symbol: 'π', left: 296, top: -22, size: 72, fontSize: 36, radius: '50%', bg: 'var(--color-neutral-50)', fg: INK, rotate: -10, depth: 'behind', duration: 7.4, delay: -3.2},
    {symbol: '∞', left: 596, top: 244, size: 66, fontSize: 32, radius: 18, bg: 'var(--color-primary-200)', fg: INK, rotate: 12, depth: 'behind', duration: 6.8, delay: -3.0},
    {symbol: '%', left: -34, top: 262, size: 62, fontSize: 30, radius: 16, bg: '#FDE8D6', fg: INK, rotate: -8, depth: 'behind', duration: 6.1, delay: -2.7},
    {symbol: '=', left: 322, top: 548, size: 68, fontSize: 34, radius: 18, bg: 'var(--color-feedback-correct-tint)', fg: INK, rotate: 8, depth: 'behind', duration: 7, delay: -3.0},
    {symbol: 'x²', left: 176, top: 566, size: 60, fontSize: 26, radius: 16, bg: 'var(--home-color-orange)', fg: INK, rotate: -6, depth: 'behind', duration: 6.4, delay: -2.8},
    {symbol: '×', left: 318, top: 300, size: 50, fontSize: 28, radius: 14, bg: 'var(--color-primary-100)', fg: INK, rotate: -12, depth: 'front', duration: 5.6, delay: -2.4},
    {symbol: '÷', left: 70, top: 626, size: 52, fontSize: 28, radius: 14, bg: 'var(--home-color-orange)', fg: INK, rotate: 10, depth: 'front', duration: 6.2, delay: -2.7},
    {symbol: '√', left: 520, top: 706, size: 56, fontSize: 30, radius: 16, bg: 'var(--color-neutral-900)', fg: 'var(--home-color-orange)', rotate: -8, depth: 'front', duration: 6.6, delay: -2.9},
    {symbol: '+', left: 608, top: -16, size: 44, fontSize: 26, radius: '50%', bg: 'var(--color-feedback-correct-tint)', fg: INK, rotate: 8, depth: 'front', duration: 5.2, delay: -2.3},
    {symbol: '−', left: -36, top: 168, size: 42, fontSize: 26, radius: 12, bg: 'var(--home-color-orange)', fg: INK, rotate: -10, depth: 'front', duration: 5.8, delay: -2.5},
    {symbol: '∑', left: 258, top: 704, size: 48, fontSize: 26, radius: 14, bg: 'var(--color-neutral-50)', fg: INK, rotate: 6, depth: 'front', duration: 6, delay: -2.6},
    {symbol: '½', left: 632, top: 468, size: 44, fontSize: 22, radius: '50%', bg: '#FDE8D6', fg: INK, rotate: -12, depth: 'front', duration: 5.4, delay: -2.3},
];

function Canvas({motionRef, className, cards, floaters, playing}) {
    return (
        <div ref={motionRef} className={className} aria-hidden="true">
            {floaters.filter((floater) => floater.depth === 'behind').map((floater) => (
                <MathFloater key={floater.symbol} {...floater} playing={playing}/>
            ))}

            {cards.map(({Widget, ...card}) => (
                <WidgetCard key={Widget.name} {...card} playing={playing}>
                    <Widget playing={playing}/>
                </WidgetCard>
            ))}

            {floaters.filter((floater) => floater.depth === 'front').map((floater) => (
                <MathFloater key={floater.symbol} {...floater} playing={playing}/>
            ))}
        </div>
    );
}

/** Positions the hero's decorative widget cards and math-symbol floaters, mobile and desktop. */
export default function HeroWidgets() {
    const mobile = useHeroWidgetsMotion();
    const desktop = useHeroWidgetsMotion();

    return (
        <>
            <Canvas
                motionRef={mobile.ref} className={clsx(styles.canvas, styles.canvasMobile)}
                cards={MOBILE_CARDS} floaters={MOBILE_FLOATERS} playing={mobile.playing}
            />
            <Canvas
                motionRef={desktop.ref} className={clsx(styles.canvas, styles.canvasDesktop)}
                cards={DESKTOP_CARDS} floaters={DESKTOP_FLOATERS} playing={desktop.playing}
            />
        </>
    );
}
